import React, { useMemo, useCallback, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch, useAuth } from "../../App";
import { toast } from "sonner";
import { Plus, ArrowUp, ArrowDown, ArrowUpDown, Archive, RotateCcw, FolderKanban, GripVertical, RefreshCw } from "lucide-react";
import UpcomingAppointmentsWidget from "../../components/UpcomingAppointmentsWidget";
import MyTasksWidget from "../../components/MyTasksWidget";
import NinaDriveSyncModal from "../../components/NinaDriveSyncModal";
import { useTranslation } from "../../i18n/LanguageProvider";
import {
  PROJECT_TYPE_LABELS, PROJECT_STATUS_LABELS,
  PROJECT_TYPE_OPTIONS, PROJECT_STATUS_OPTIONS, getProjectTypeColor
} from "../../config/projectTypeColors";
import { MitarbeiterCell } from "./MitarbeiterCell";
import { CreateProjectDialog } from "./CreateProjectDialog";
import { ArchiveProjectDialog } from "./ArchiveProjectDialog";

const API = "/api";

const billingLabels = {
  stunden_abrechenbar: "Stunden",
  festpreis: "Festpreis",
  pauschal: "Pauschal",
  nicht_abrechenbar: "N/A",
  sonstiges: "Sonstiges",
};

const emptyForm = {
  name: "",
  description: "",
  project_type: "sonstiges",
  budget: "",
  planned_hours: "",
  km_cost_per_km: "0.30",
};

export default function ProjectsModule() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "projektleiter";
  const { t } = useTranslation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [flashRow, setFlashRow] = useState(null);
  const [sortKey, setSortKey] = useState("manual");
  const [sortDir, setSortDir] = useState("asc");
  const [filterText, setFilterText] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  // Drag & Drop für manuelle Projekt-Sortierung
  const [dragOverProjectId, setDragOverProjectId] = useState(null);
  const dragProjectIdRef = useRef(null);

  // Horizontal-Scroll Container Ref + Wheel Handler (vertical wheel → horizontal scroll)
  const scrollRef = useRef(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e) => {
      // Nur wenn Tabelle horizontal scrollbar ist UND wheel hauptsaechlich vertikal kommt
      if (el.scrollWidth <= el.clientWidth) return;
      const dy = e.deltaY;
      const dx = e.deltaX;
      if (Math.abs(dy) > Math.abs(dx) && dy !== 0) {
        el.scrollLeft += dy;
        e.preventDefault();
      }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["projects"],
    queryFn: async () => {
      const res = await authFetch(`${API}/projects`);
      if (!res.ok) throw new Error("Fehler");
      return res.json();
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
  });

  const { data: employees = [] } = useQuery({
    queryKey: ["employees-directory"],
    queryFn: async () => {
      const res = await authFetch(`${API}/users/directory`);
      if (!res.ok) return [];
      return res.json();
    },
    refetchInterval: 120000,
  });

  const updateProject = useMutation({
    mutationFn: async ({ id, updates }) => {
      const res = await authFetch(`${API}/projects/${id}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      });
      if (!res.ok) throw new Error("Fehler");
      return res.json();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      setFlashRow(variables.id);
      setTimeout(() => setFlashRow(null), 800);
    },
    onError: () => toast.error("Fehler beim Speichern"),
  });

  const handleInlineChange = useCallback(
    (id, field, value) => {
      updateProject.mutate({ id, updates: { [field]: value } });
    },
    [updateProject]
  );

  const archiveProject = useMutation({
    mutationFn: async (id) => {
      const res = await authFetch(`${API}/projects/${id}/archive`, { method: "PATCH" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Fehler"); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Projekt archiviert");
      setArchiveTarget(null);
    },
    onError: (e) => toast.error(e.message || "Fehler beim Archivieren"),
  });

  const restoreProject = useMutation({
    mutationFn: async (id) => {
      const res = await authFetch(`${API}/projects/${id}/restore`, { method: "PATCH" });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || "Fehler"); }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      toast.success("Projekt wiederhergestellt");
    },
    onError: (e) => toast.error(e.message || "Fehler beim Wiederherstellen"),
  });

  const canEditProject = useCallback((project) => {
    if (isAdmin) return true;
    const pp = user?.project_permissions || {};
    if (pp.can_edit_all_projects) return true;
    const userId = user?.id;
    const assigned = project.assigned_users || [];
    return assigned.includes(userId) || project.project_manager === userId;
  }, [isAdmin, user]);

  const canArchive = isAdmin || user?.project_permissions?.can_archive_projects;

  const handleCreateProject = async () => {
    if (!formData.name.trim()) {
      toast.error("Projektname ist erforderlich");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description,
        project_type: formData.project_type,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        planned_hours: formData.planned_hours ? parseFloat(formData.planned_hours) : null,
        km_cost_per_km: formData.km_cost_per_km ? parseFloat(formData.km_cost_per_km) : 0.30,
      };
      const res = await authFetch(`${API}/projects`, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        queryClient.invalidateQueries({ queryKey: ["projects"] });
        toast.success("Projekt erstellt");
        setDialogOpen(false);
        setFormData(emptyForm);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Fehler beim Erstellen");
      }
    } catch {
      toast.error("Fehler beim Erstellen");
    } finally {
      setSaving(false);
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  // ── Drag & Drop: Projekt-Reihenfolge persistieren ──
  const handleProjectDragStart = (e, projectId) => {
    if (!isAdmin) return;
    if (sortKey !== "manual") {
      e.preventDefault();
      toast.info("Spalten-Sortierung aktiv — klicke auf 'Nr.' im Header, um manuelle Sortierung zu aktivieren");
      return;
    }
    dragProjectIdRef.current = projectId;
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", projectId); } catch { /* noop */ }
  };
  const handleProjectDragOver = (e, projectId) => {
    if (!isAdmin || sortKey !== "manual" || !dragProjectIdRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (projectId !== dragOverProjectId) setDragOverProjectId(projectId);
  };
  const handleProjectDragLeave = () => setDragOverProjectId(null);
  const handleProjectDrop = async (e, dropProjectId) => {
    if (!isAdmin) return;
    e.preventDefault();
    const fromId = dragProjectIdRef.current;
    dragProjectIdRef.current = null;
    setDragOverProjectId(null);
    if (!fromId || fromId === dropProjectId) return;
    // Reorder in der sichtbaren Liste (displayData), dann auf alle projects mappen
    const visibleIds = displayData.map(p => p.id);
    const fromIdx = visibleIds.indexOf(fromId);
    const dropIdx = visibleIds.indexOf(dropProjectId);
    if (fromIdx < 0 || dropIdx < 0) return;
    const newVisible = [...visibleIds];
    newVisible.splice(fromIdx, 1);
    newVisible.splice(dropIdx, 0, fromId);
    // Mappe die neue sichtbare Reihenfolge zurück auf alle Projekte (verstecke Filter-Lücken bleiben)
    const visibleSet = new Set(visibleIds);
    const allOrdered = [];
    let visibleCursor = 0;
    for (const p of projects) {
      if (visibleSet.has(p.id)) {
        const targetId = newVisible[visibleCursor++];
        allOrdered.push(projects.find(x => x.id === targetId));
      } else {
        allOrdered.push(p);
      }
    }
    // Optimistic: React-Query Cache direkt setzen
    queryClient.setQueryData(["projects"], allOrdered);
    try {
      const res = await authFetch(`${API}/projects/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_ids: allOrdered.map(p => p.id) }),
      });
      if (!res.ok) {
        toast.error("Reihenfolge konnte nicht gespeichert werden");
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else {
        toast.success("Reihenfolge aktualisiert");
      }
    } catch {
      toast.error("Reihenfolge konnte nicht gespeichert werden");
      queryClient.invalidateQueries({ queryKey: ["projects"] });
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-2.5 h-2.5" style={{ color: "var(--tga-text-dim)" }} />;
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3" style={{ color: "var(--tga-text-secondary)" }} />
      : <ArrowDown className="w-3 h-3" style={{ color: "var(--tga-text-secondary)" }} />;
  };

  const displayData = useMemo(() => {
    let d = [...projects];
    if (filterText) {
      const q = filterText.toLowerCase();
      d = d.filter(p => p.name?.toLowerCase().includes(q) || p.project_number?.toLowerCase().includes(q) || p.short_code?.toLowerCase().includes(q));
    }
    if (filterCustomer) {
      const q = filterCustomer.toLowerCase();
      d = d.filter(p => (p.customer_label || p.client_name || "").toLowerCase().includes(q));
    }
    if (filterStatus) d = d.filter(p => p.status === filterStatus);
    else d = d.filter(p => p.status !== "archiviert");
    if (filterType) d = d.filter(p => p.project_type === filterType);
    d.sort((a, b) => {
      if (sortKey === "manual") {
        const aOrder = a.sort_order ?? 10_000_000;
        const bOrder = b.sort_order ?? 10_000_000;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return String(a.name || "").localeCompare(String(b.name || ""), "de");
      }
      const av = a[sortKey] ?? "";
      const bv = b[sortKey] ?? "";
      const cmp = String(av).localeCompare(String(bv), "de", { numeric: true });
      return sortDir === "asc" ? cmp : -cmp;
    });
    return d;
  }, [projects, filterText, filterCustomer, filterStatus, filterType, sortKey, sortDir]);

  const empOptions = useMemo(() => employees.filter(e => e.is_active !== false).map(e => ({ value: e.id, label: e.name })), [employees]);
  const allEmpLookup = useMemo(() => {
    const map = {};
    employees.forEach(e => { map[e.id] = e.name; });
    return map;
  }, [employees]);

  const handleToggleAssignment = useCallback(
    (projectId, userId, currentAssigned) => {
      const isAssigned = currentAssigned.includes(userId);
      const newAssigned = isAssigned
        ? currentAssigned.filter(id => id !== userId)
        : [...currentAssigned, userId];
      updateProject.mutate({ id: projectId, updates: { assigned_users: newAssigned } });
    },
    [updateProject]
  );

  const columns = [
    { key: "project_number", label: "Nr.", w: 60 },
    { key: "name", label: "Projekt", w: 220 },
    { key: "customer_label", label: "Kunde", w: 160 },
    { key: "short_code", label: "Kuerzel", w: 60 },
    { key: "status", label: "Status", w: 110 },
    { key: "project_type", label: "Typ", w: 160 },
    { key: "project_manager", label: "Projektleiter", w: 160 },
    { key: "assigned_users", label: "Mitarbeiter", w: 130 },
    { key: "billing_type", label: "Abrechnung", w: 90 },
    { key: "start_date", label: "Start", w: 95 },
    { key: "_actions", label: "", w: 40 },
  ];

  /* ── Dark Theme Style Constants ── */
  const thStyle = "text-left text-[11px] font-semibold uppercase tracking-wider px-2 py-2 select-none";
  const tdStyle = "px-2 py-2 text-[12px]";

  /* ── Sticky-First-Columns (Nr. + Projekt bleiben beim Horizontal-Scroll sichtbar) ── */
  const STICKY_COL0_W = 60;   // muss zu columns[0].w passen
  const STICKY_COL1_LEFT = STICKY_COL0_W;
  const stickyShadow = "2px 0 6px -2px rgba(0,0,0,0.45)"; // sanfter rechter Schatten

  // Header (z-index hoch, damit über den scrollenden Body bleibt)
  const headerCol0Sticky = {
    position: "sticky", left: 0, zIndex: 5,
    background: "var(--tga-bg-elevated)",
  };
  const headerCol1Sticky = {
    position: "sticky", left: STICKY_COL1_LEFT, zIndex: 5,
    background: "var(--tga-bg-elevated)",
    boxShadow: stickyShadow,
  };
  // Filter-Row (gleiches z, andere Background)
  const filterCol0Sticky = {
    position: "sticky", left: 0, zIndex: 4,
    background: "var(--tga-bg-card)",
  };
  const filterCol1Sticky = {
    position: "sticky", left: STICKY_COL1_LEFT, zIndex: 4,
    background: "var(--tga-bg-card)",
    boxShadow: stickyShadow,
  };
  // Body (z-index unter Header)
  const bodyCol0Sticky = {
    position: "sticky", left: 0, zIndex: 2,
  };
  const bodyCol1Sticky = {
    position: "sticky", left: STICKY_COL1_LEFT, zIndex: 2,
    boxShadow: stickyShadow,
  };
  const selectStyle = "w-full text-[12px] py-1 pl-1 pr-5 focus:outline-none cursor-pointer rounded-sm transition-colors duration-150";

  return (
    <div data-testid="projects-module">
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <UpcomingAppointmentsWidget />
          <MyTasksWidget />
        </div>
      )}
      {!isAdmin && <MyTasksWidget />}

      {/* ── Toolbar ── */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: "var(--tga-blue-soft)" }}
          >
            <FolderKanban className="w-4 h-4" style={{ color: "var(--tga-blue)" }} />
          </div>
          <div>
            <h2
              className="text-[15px] font-semibold"
              style={{ color: "var(--tga-text-bright)" }}
              data-testid="table-title"
            >
              Projekte
            </h2>
            <span className="text-[11px]" style={{ color: "var(--tga-text-muted)" }}>
              {displayData.length} Eintraege
            </span>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSyncModalOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md transition-all duration-200 border"
              style={{
                background: "var(--tga-bg-card)",
                borderColor: "var(--tga-border)",
                color: "var(--tga-text-bright)",
              }}
              data-testid="drive-sync-all-btn"
              title="Alle Drive-verknüpften Projekte synchronisieren"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Drive synchronisieren
            </button>
            <button
              onClick={() => setDialogOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-md transition-all duration-200"
              style={{
                background: "var(--tga-blue)",
                color: "#fff",
                boxShadow: "0 2px 8px rgba(59,130,246,0.25)",
              }}
              data-testid="add-row-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Neues Projekt
            </button>
          </div>
        )}
      </div>

      {/* ── Table Card ── */}
      <div
        className="relative"
        style={{
          background: "var(--tga-bg-card)",
          border: "1px solid var(--tga-border)",
          borderRadius: "var(--tga-radius-lg)",
          boxShadow: "var(--tga-shadow-card)",
          overflow: "hidden", // clipt nur die Außenkanten für rounded corners
        }}
        data-testid="table-container"
      >
        {/* Fade-Gradient rechts: visueller Hinweis "es geht noch weiter →" */}
        <div
          aria-hidden="true"
          className="absolute right-0 top-0 bottom-0 pointer-events-none"
          style={{
            width: "32px",
            background: "linear-gradient(to right, transparent, var(--tga-bg-card) 80%)",
            zIndex: 4,
          }}
        />
        <div
          ref={scrollRef}
          className="tga-table-scroll"
          style={{
            overflowX: "scroll", // FORCE Scrollbar immer sichtbar
            overflowY: "hidden",
            WebkitOverflowScrolling: "touch",
          }}
          data-testid="projects-module-scroll"
        >
          <table className="w-full" style={{ borderCollapse: "collapse", minWidth: "1400px", tableLayout: "auto" }}>
            <thead>
              {/* ── Header Row ── */}
              <tr style={{ background: "var(--tga-bg-elevated)", borderBottom: "1px solid var(--tga-border-strong)" }}>
                {columns.map((col, idx) => (
                  <th
                    key={col.key}
                    className={thStyle}
                    style={{
                      width: col.w,
                      borderRight: "1px solid var(--tga-border)",
                      color: "var(--tga-text-muted)",
                      ...(idx === 0 ? headerCol0Sticky : idx === 1 ? headerCol1Sticky : {}),
                    }}
                  >
                    {col.key !== "_actions" ? (
                      <div className="flex items-center gap-1 cursor-pointer" onClick={() => toggleSort(col.key)}>
                        {col.label} <SortIcon col={col.key} />
                      </div>
                    ) : null}
                  </th>
                ))}
              </tr>
              {/* ── Filter Row ── */}
              <tr style={{ background: "var(--tga-bg-card)", borderBottom: "1px solid var(--tga-border)" }}>
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)", ...filterCol0Sticky }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)", ...filterCol1Sticky }}>
                  <input
                    type="text"
                    value={filterText}
                    onChange={e => setFilterText(e.target.value)}
                    placeholder="Suchen..."
                    className="w-full px-2 py-1 text-[11px] rounded-sm focus:outline-none"
                    style={{
                      background: "var(--tga-bg-content)",
                      border: "1px solid var(--tga-border)",
                      color: "var(--tga-text-primary)",
                    }}
                    data-testid="filter-name"
                  />
                </th>
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }}>
                  <input
                    type="text"
                    value={filterCustomer}
                    onChange={e => setFilterCustomer(e.target.value)}
                    placeholder="Kunde..."
                    className="w-full px-2 py-1 text-[11px] rounded-sm focus:outline-none"
                    style={{
                      background: "var(--tga-bg-content)",
                      border: "1px solid var(--tga-border)",
                      color: "var(--tga-text-primary)",
                    }}
                    data-testid="filter-customer"
                  />
                </th>
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }}>
                  <select
                    value={filterStatus}
                    onChange={e => setFilterStatus(e.target.value)}
                    className="w-full px-1.5 py-1 text-[11px] rounded-sm focus:outline-none cursor-pointer"
                    style={{
                      background: "var(--tga-bg-content)",
                      border: "1px solid var(--tga-border)",
                      color: "var(--tga-text-primary)",
                    }}
                    data-testid="filter-status"
                  >
                    <option value="">Alle</option>
                    {PROJECT_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </th>
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }}>
                  <select
                    value={filterType}
                    onChange={e => setFilterType(e.target.value)}
                    className="w-full px-1.5 py-1 text-[11px] rounded-sm focus:outline-none cursor-pointer"
                    style={{
                      background: "var(--tga-bg-content)",
                      border: "1px solid var(--tga-border)",
                      color: "var(--tga-text-primary)",
                    }}
                    data-testid="filter-type"
                  >
                    <option value="">Alle</option>
                    {PROJECT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </th>
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
                <th className="px-1.5 py-1.5" style={{ borderRight: "1px solid var(--tga-border)" }} />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-8" style={{ color: "var(--tga-text-muted)" }}>
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--tga-blue)", borderTopColor: "transparent" }} />
                      Lade Daten...
                    </div>
                  </td>
                </tr>
              ) : displayData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-12">
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "var(--tga-border)" }}>
                        <FolderKanban className="w-5 h-5" style={{ color: "var(--tga-text-dim)" }} />
                      </div>
                      <span className="text-[12px]" style={{ color: "var(--tga-text-muted)" }}>Keine Projekte vorhanden</span>
                    </div>
                  </td>
                </tr>
              ) : (
                displayData.map((p, i) => {
                  const typeColor = getProjectTypeColor(p.project_type);
                  const isFlash = flashRow === p.id;
                  const isDragOver = dragOverProjectId === p.id;
                  return (
                    <tr
                      key={p.id}
                      className="group tga-sticky-row transition-colors duration-150"
                      onDragOver={(e) => handleProjectDragOver(e, p.id)}
                      onDragLeave={handleProjectDragLeave}
                      onDrop={(e) => handleProjectDrop(e, p.id)}
                      style={{
                        background: isFlash
                          ? "var(--tga-blue-soft)"
                          : isDragOver
                            ? "rgba(59,130,246,0.06)"
                            : "var(--tga-bg-content)",
                        borderTop: isDragOver ? "2px solid var(--tga-blue)" : undefined,
                        borderBottom: "1px solid var(--tga-border)",
                        cursor: "pointer",
                      }}
                      data-flash={isFlash ? "1" : "0"}
                      data-testid={`project-row-${p.id}`}
                    >
                      {/* Nr. (sticky) — mit Drag-Handle für Admin/Projektleiter */}
                      <td className={tdStyle} style={{
                        borderRight: "1px solid var(--tga-border)",
                        color: "var(--tga-text-muted)",
                        fontFamily: "monospace",
                        ...bodyCol0Sticky,
                        background: isFlash ? "var(--tga-blue-soft)" : (isDragOver ? "rgba(59,130,246,0.06)" : "var(--tga-bg-content)"),
                      }}>
                        <div className="flex items-center gap-1.5">
                          {isAdmin && (
                            <span
                              draggable={sortKey === "manual"}
                              onDragStart={(e) => handleProjectDragStart(e, p.id)}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (sortKey !== "manual") {
                                  setSortKey("manual");
                                  setSortDir("asc");
                                  toast.success("Manuelle Sortierung aktiv — jetzt ziehen");
                                }
                              }}
                              title={sortKey === "manual" ? "Ziehen zum Sortieren" : "Klicken, um manuelle Sortierung zu aktivieren"}
                              data-testid={`project-drag-${p.id}`}
                              style={{
                                cursor: sortKey === "manual" ? "grab" : "pointer",
                                opacity: sortKey === "manual" ? 0.85 : 0.4,
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <GripVertical className="w-3 h-3" />
                            </span>
                          )}
                          <span>{p.project_number || "\u2014"}</span>
                        </div>
                      </td>

                      {/* Projekt (sticky) */}
                      <td
                        className={`${tdStyle} cursor-pointer`}
                        style={{
                          borderRight: "1px solid var(--tga-border)",
                          ...bodyCol1Sticky,
                          background: isFlash ? "var(--tga-blue-soft)" : "var(--tga-bg-content)",
                        }}
                        onClick={() => navigate(`/app/projekte/${p.id}/dashboard`)}
                      >
                        <span
                          className="font-semibold text-[13px] truncate block transition-colors duration-150"
                          style={{ color: "var(--tga-text-bright)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = "var(--tga-blue)")}
                          onMouseLeave={(e) => (e.currentTarget.style.color = "var(--tga-text-bright)")}
                        >
                          {p.name}
                        </span>
                        {(() => {
                          // Projektübersicht zeigt IMMER Kurzbeschreibung.
                          // Fallback: Ausführliche Beschreibung (max 120 Zeichen).
                          const short = (p.short_description || "").trim();
                          const long = (p.description || "").trim();
                          const display = short || (long ? (long.length > 120 ? long.slice(0, 120).trimEnd() + "…" : long) : "");
                          if (!display) return null;
                          return (
                            <span
                              className="block truncate text-[11px] mt-0.5"
                              style={{ color: "#8B97A8" }}
                              title={short || long}
                              data-testid={`project-description-${p.id}`}
                            >
                              {display}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Kunde */}
                      <td
                        className={tdStyle}
                        style={{ borderRight: "1px solid var(--tga-border)" }}
                        data-testid={`project-customer-${p.id}`}
                      >
                        {p.customer_label ? (
                          <span
                            className="text-[12px] truncate block"
                            style={{ color: "var(--tga-text-primary)" }}
                            title={p.customer_kundennummer ? `${p.customer_label} (${p.customer_kundennummer})` : p.customer_label}
                          >
                            {p.customer_label}
                            {p.customer_kundennummer && (
                              <span className="ml-1 text-[10px]" style={{ color: "var(--tga-text-dim)" }}>
                                · {p.customer_kundennummer}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: "var(--tga-text-dim)" }}>—</span>
                        )}
                      </td>

                      {/* Kuerzel */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-muted)", fontFamily: "monospace" }}>
                        {p.short_code || "\u2014"}
                      </td>

                      {/* Status */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)" }}>
                        {canEditProject(p) ? (
                          <select
                            value={p.status}
                            onChange={e => handleInlineChange(p.id, "status", e.target.value)}
                            className={selectStyle}
                            style={{
                              background: "transparent",
                              border: "1px solid transparent",
                              color: "var(--tga-text-secondary)",
                            }}
                            onFocus={(e) => { e.target.style.borderColor = "var(--tga-border-strong)"; e.target.style.background = "var(--tga-bg-card)"; }}
                            onBlur={(e) => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; }}
                            data-testid={`status-select-${p.id}`}
                          >
                            {PROJECT_STATUS_OPTIONS.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ color: "var(--tga-text-secondary)" }}>{PROJECT_STATUS_LABELS[p.status] || p.status}</span>
                        )}
                      </td>

                      {/* Typ */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)" }}>
                        {canEditProject(p) ? (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: typeColor }}
                              data-testid={`type-square-${p.id}`}
                            />
                            <select
                              value={p.project_type}
                              onChange={e => handleInlineChange(p.id, "project_type", e.target.value)}
                              className={selectStyle}
                              style={{
                                background: "transparent",
                                border: "1px solid transparent",
                                color: "var(--tga-text-secondary)",
                              }}
                              onFocus={(e) => { e.target.style.borderColor = "var(--tga-border-strong)"; e.target.style.background = "var(--tga-bg-card)"; }}
                              onBlur={(e) => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; }}
                              data-testid={`type-select-${p.id}`}
                            >
                              {PROJECT_TYPE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2.5 h-2.5 shrink-0 rounded-[2px]"
                              style={{ backgroundColor: typeColor }}
                              data-testid={`type-square-${p.id}`}
                            />
                            <span style={{ color: "var(--tga-text-secondary)" }}>{PROJECT_TYPE_LABELS[p.project_type] || p.project_type}</span>
                          </div>
                        )}
                      </td>

                      {/* Projektleiter */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)" }}>
                        {canEditProject(p) ? (
                          <select
                            value={p.project_manager || ""}
                            onChange={e => handleInlineChange(p.id, "project_manager", e.target.value || "")}
                            className={selectStyle}
                            style={{
                              background: "transparent",
                              border: "1px solid transparent",
                              color: "var(--tga-text-secondary)",
                            }}
                            onFocus={(e) => { e.target.style.borderColor = "var(--tga-border-strong)"; e.target.style.background = "var(--tga-bg-card)"; }}
                            onBlur={(e) => { e.target.style.borderColor = "transparent"; e.target.style.background = "transparent"; }}
                            data-testid={`manager-select-${p.id}`}
                          >
                            <option value="">-- Keine --</option>
                            {empOptions.map(o => (
                              <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                          </select>
                        ) : (
                          <span style={{ color: "var(--tga-text-secondary)" }} data-testid={`manager-display-${p.id}`}>
                            {p.project_manager
                              ? (allEmpLookup[p.project_manager] || empOptions.find(e => e.value === p.project_manager)?.label || p.project_manager_name || "\u2014")
                              : "\u2014"}
                          </span>
                        )}
                      </td>

                      {/* Mitarbeiter */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)" }}>
                        <MitarbeiterCell
                          project={p}
                          empOptions={empOptions}
                          allEmpLookup={allEmpLookup}
                          onToggle={handleToggleAssignment}
                          canEdit={canEditProject(p)}
                        />
                      </td>

                      {/* Abrechnung */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>
                        {billingLabels[p.billing_type] || p.billing_type}
                      </td>

                      {/* Start */}
                      <td className={tdStyle} style={{ borderRight: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}>
                        {p.start_date || "\u2014"}
                      </td>

                      {/* Actions */}
                      <td className={`${tdStyle} text-center`}>
                        {p.status === "archiviert" && isAdmin ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); restoreProject.mutate(p.id); }}
                            className="p-1 rounded transition-colors duration-150 opacity-0 group-hover:opacity-100"
                            style={{ color: "var(--tga-text-dim)" }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-green)"; e.currentTarget.style.background = "var(--tga-green-soft)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-text-dim)"; e.currentTarget.style.background = "transparent"; }}
                            title="Wiederherstellen"
                            data-testid={`restore-project-${p.id}`}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </button>
                        ) : canArchive && p.status !== "archiviert" ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); setArchiveTarget(p); }}
                            className="p-1 rounded transition-colors duration-150 opacity-0 group-hover:opacity-100"
                            style={{ color: "var(--tga-text-dim)" }}
                            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--tga-amber)"; e.currentTarget.style.background = "var(--tga-amber-soft)"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--tga-text-dim)"; e.currentTarget.style.background = "transparent"; }}
                            title="Archivieren"
                            data-testid={`archive-project-${p.id}`}
                          >
                            <Archive className="w-3.5 h-3.5" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setFormData(emptyForm); }}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleCreateProject}
        saving={saving}
      />

      <ArchiveProjectDialog
        target={archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={(id) => archiveProject.mutate(id)}
        isPending={archiveProject.isPending}
      />

      <NinaDriveSyncModal
        open={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        onFinished={() => queryClient.invalidateQueries({ queryKey: ["projects"] })}
      />
    </div>
  );
}
